/**
 * The hub's requiem: a generated score, no sample, no download.
 *
 * An organ-like F-minor add9 chord held by paired triangle/sine oscillators, each voice
 * breathing on its own slow LFO through one low-pass filter, and a low bell that tolls
 * every seven seconds or so. It rides the SoundManager's ambience bus so the settings
 * sliders and the mute apply, and it fades in over five seconds and out over one and a
 * half so entering and leaving Lanternfall never clicks.
 *
 * Start it from the hub (after the Enter gesture has unlocked audio) and stop it on deploy.
 */

import type { SoundManager } from './sound.js';

/** F2, C3, F3, Ab3, C4, G4: the chord, low to high. */
const VOICES = [87.31, 130.81, 174.61, 207.65, 261.63, 392];
const BELL_HZ = 174.61;
const FADE_IN_S = 5;
const FADE_OUT_S = 1.5;
/** Overall level into the ambience bus; the bus itself carries the player's volume. */
const LEVEL = 0.15;

export class Requiem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly nodes: AudioScheduledSourceNode[] = [];
  private bellTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly sound: SoundManager) {}

  get playing(): boolean {
    return this.ctx !== null;
  }

  start(): void {
    if (this.ctx) return;
    const out = this.sound.ambienceBus();
    if (!out) return;
    const { ctx, bus } = out;
    this.ctx = ctx;

    const master = ctx.createGain();
    master.gain.value = 0.0001;
    master.connect(bus);
    master.gain.exponentialRampToValueAtTime(LEVEL, ctx.currentTime + FADE_IN_S);
    this.master = master;

    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 700;
    lp.connect(master);

    VOICES.forEach((f, i) => {
      const g = ctx.createGain();
      g.gain.value = 0.1 / (1 + i * 0.3);
      g.connect(lp);
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.04 + i * 0.01;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.04;
      lfo.connect(lfoGain);
      lfoGain.connect(g.gain);
      lfo.start();
      this.nodes.push(lfo);
      for (const [type, mult, detune, level] of [
        ['triangle', 1, -4, 1],
        ['sine', 2, 4, 0.35],
      ] as const) {
        const o = ctx.createOscillator();
        o.type = type;
        o.frequency.value = f * mult;
        o.detune.value = detune;
        const og = ctx.createGain();
        og.gain.value = level;
        o.connect(og);
        og.connect(g);
        o.start();
        this.nodes.push(o);
      }
    });

    const toll = (): void => {
      if (!this.ctx || !this.master) return;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = BELL_HZ;
      g.gain.value = 0.0001;
      g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 6);
      o.connect(g);
      g.connect(this.master);
      o.start();
      o.stop(ctx.currentTime + 6.2);
      this.bellTimer = setTimeout(toll, 6500 + Math.random() * 2000);
    };
    this.bellTimer = setTimeout(toll, 2500);
  }

  stop(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    if (this.bellTimer) clearTimeout(this.bellTimer);
    this.bellTimer = null;
    master.gain.cancelScheduledValues(ctx.currentTime);
    master.gain.setValueAtTime(Math.max(0.0001, master.gain.value), ctx.currentTime);
    master.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + FADE_OUT_S);
    const nodes = [...this.nodes];
    this.nodes.length = 0;
    setTimeout(
      () => {
        for (const n of nodes) {
          try {
            n.stop();
          } catch {
            /* already stopped */
          }
        }
        master.disconnect();
      },
      FADE_OUT_S * 1000 + 100,
    );
    this.ctx = null;
    this.master = null;
  }
}
